import { ChallengeConfig, OtherGamesType } from './types';

export enum Environment {
	DEVELOPMENT = 'development',
	PRODUCTION = 'production',
}

export enum ServerEnvironment {
	PRODUCTION = 'PRODUCTION',
	STAGING = 'STAGING',
	TESTING = 'TESTING',
	UNKNOWN = 'UNKNOWN',
}

export interface SdkConfig {
	mode: Environment
}

export interface Parameters {
	baseUrl?: string;
	lpsUrl?: string;
	ipp?: {
		ippFbUrl?: string;
		ippGoogleUrl?: string;
		ippAppleUrl?: string;
	};
	portalBar?: {
		html?: string;
		js?: string;
		css?: string;
	};
	gameId?: string;
	marketId?: string;
	locale?: string;
	country?: string;
	monolingual?: boolean;
	landingPageId?: string;
	partnerPrefix?: string;
	campaignId?: string;
	bannerId?: string;
	creativeId?: string | null;
	publisherId?: string;
	backendRequestEventId?: string;
	cdn?: string;
	bundle?: string;
	concept?: string;
	snippets?: {
		optional?: string[];
		mandatory?: string[];
	};
	portalSsoError?: {
		errorType?: string;
		errorTranslationKey?: string;
	};
	ssoConfirmationPath?: {
		path?: string;
	};
	forgotPw?: {
		token?: string;
		errorCode?: string;
	};
	pcn?: string;
	deeplink?: string;
	forwardParams?: boolean;
	bot?: boolean;
	spam?: boolean;
	operatingSystem?: string;
	deviceType?: string;
	environment?: string;
	social?: {
		facebook?: string;
		youtube?: string;
		discord?: string;
	};
	cdnUrlPrefix?: string;
	nickname?: string;
	isMobileRequest?: boolean;
	otherGames?: OtherGamesType;
	challengeConfig?: ChallengeConfig;
}

export class ParametersConfig<T extends Parameters> {
	protected readonly config: T;

	constructor(config: T) {
		this.config = config;
	}

	get<K extends keyof T>(name: K): T[K] {
		if (this.config[name] === undefined) {
			throw new Error(`Trying to access non-existent config parameter "${String(name)}"`);
		}
		return this.config[name];
	}

	set<K extends keyof T>(name: K, value: T[K]): void {
		this.config[name] = value;
	}
}

export class Config extends ParametersConfig<Parameters> {
	getBaseUrl(): string | null {
		return this.config.baseUrl || null;
	}

	getLpsUrl(): string {
		return this.get('lpsUrl')!;
	}

	getIppFbUrl(): string {
		return this.get('ipp')!.ippFbUrl!;
	}

	getIppGoogleUrl(): string {
		return this.get('ipp')!.ippGoogleUrl!;
	}

	getIppAppleUrl(): string {
		return this.get('ipp')!.ippAppleUrl!;
	}

	isPortalBar(): boolean {
		return this.config.portalBar !== undefined;
	}

	getPortalBarHtml(): string | null {
		return this.config.portalBar?.html || null;
	}

	getPortalBarJs(): string | null {
		return this.config.portalBar?.js || null;
	}

	getPortalBarCss(): string | null {
		return this.config.portalBar?.css || null;
	}

	getGameId(): string {
		return this.get('gameId')!;
	}

	getMarketId(): string {
		return this.get('marketId')!;
	}

	getLocale(): string {
		return this.get('locale')!;
	}

	getCountry(): string | null {
		return this.get('country')!;
	}

	isMonolingual(): boolean {
		return this.config.monolingual ?? false;
	}

	getLandingPageId(): string {
		return this.get('landingPageId')!;
	}

	getPartnerPrefix(): string {
		return this.get('partnerPrefix')!;
	}

	getCampaignId(): string {
		return this.get('campaignId')!;
	}

	getBannerId(): string {
		return this.get('bannerId')!;
	}

	getCreativeId(): string {
		return this.get('creativeId')!;
	}

	getPublisherId(): string {
		return this.get('publisherId')!;
	}

	getBackendRequestEventId(): string {
		return this.get('backendRequestEventId')!;
	}

	getCdnAssetUrlPrefix(): string {
		// TODO: Still use bundle for now so we can merge this into master without breaking bundle uploads
		return `${this.config.cdn}/bundle/${this.config.bundle}`;
	}

	getCdnAssetUrl(path: string): string {
		return `${this.getCdnAssetUrlPrefix()}/${path}`;
	}

	getOptionalSnippets(): string[] {
		if (!this.config.snippets || !this.config.snippets.optional) {
			throw new Error('Optional tracking snippets are not set!');
		}
		return this.config.snippets.optional;
	}

	getMandatorySnippets(): string[] {
		if (!this.config.snippets || !this.config.snippets.mandatory) {
			throw new Error('Mandatory tracking snippets are not set!');
		}
		return this.config.snippets.mandatory;
	}

	getPortalSsoErrorType(): string | null {
		return this.config.portalSsoError?.errorType || null;
	}

	getPortalSsoErrorTranslationKey(): string | null {
		return this.config.portalSsoError?.errorTranslationKey || null;
	}

	getSsoConfirmationPath(): string | null {
		return this.config.ssoConfirmationPath?.path || null;
	}

	getPasswordForgottenToken(): string | null {
		return this.config.forgotPw?.token || null;
	}

	getPasswordForgottenErrorCode(): string | null {
		return this.config.forgotPw?.errorCode || null;
	}

	getPcn(): string | null {
		return this.config.pcn || null;
	}

	getSingularDeepLink(): string | null {
		return this.config.deeplink || null;
	}

	isSingularForwardParams(): boolean {
		return this.config.forwardParams || false;
	}

	isBot(): boolean {
		return this.config.bot || false;
	}

	isSpam(): boolean {
		return this.config.spam || false;
	}

	getOperatingSystem(): string {
		return this.get('operatingSystem')!;
	}

	getDeviceType(): string {
		return this.get('deviceType')!;
	}

	isMobileRequest(): boolean {
		return this.getDeviceType().toLowerCase() === 'mobile';
	}

	getEnvironment(): string {
		return this.get('environment')!;
	}

	getOtherGames(): OtherGamesType {
		return this.config.otherGames || {};
	}

	getChallengeConfig(): ChallengeConfig|undefined {
		return this.config.challengeConfig;
	}
}

export class DesignParams extends ParametersConfig<Parameters> { }

export interface RuntimeConfig {
	config: Parameters;
	designParams: Parameters;
}

